import { useCallback, useEffect, useState } from 'react'
import { apiErrorText } from '@/lib/api-error'
import { fetchKiLauf, kiUebernehmen, kiVerwerfen, type KiZeile } from '../api/calculation-ai.api'
import { titelVon } from './KiVorschlaege'

/**
 * Seit AP-18 Phase E an zwei Stellen: in der Leiste der Kalkulation (`AssistentLeiste.tsx`) und — als
 * Beilage unter einer Antwort — in der Leiste des allgemeinen Assistenten (`../assistent-beilagen.tsx`).
 */

/** Die Vorschlagsliste im Verlauf — Häkchen und **Übernehmen** darunter (8.1, 9.). */
export function KiLaufVorschlaege({ kalkulationId, runId, beschreibbar, onGeaendert }: { kalkulationId: string; runId: string; beschreibbar: boolean; onGeaendert: () => void }) {
  const [zeilen, setZeilen] = useState<KiZeile[] | null>(null)
  const [haken, setHaken] = useState<Set<string>>(new Set())
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  const laden = useCallback(() => {
    void fetchKiLauf(kalkulationId, runId)
      .then((l) => {
        setZeilen(l.lines)
        setHaken(new Set(l.lines.filter((z) => z.status === 'OPEN' && z.preselected).map((z) => z.id)))
      })
      .catch((e) => setFehler(apiErrorText(e, 'Die Vorschläge ließen sich nicht laden.')))
  }, [kalkulationId, runId])
  useEffect(laden, [laden])

  async function tu(schritt: () => Promise<unknown>) {
    setLaeuft(true)
    setFehler(null)
    try {
      await schritt()
      laden()
      onGeaendert()
    } catch (e) {
      setFehler(apiErrorText(e, 'Das ließ sich nicht ausführen.'))
    } finally {
      setLaeuft(false)
    }
  }

  if (!zeilen) return fehler ? <p className="text-xs text-destructive">{fehler}</p> : null
  const offen = zeilen.filter((z) => z.status === 'OPEN')
  const erledigt = zeilen.length - offen.length
  return (
    <div className="rounded-md border border-border/70 bg-muted/30 p-2">
      <div className="mb-1 text-xs font-medium text-muted-foreground">
        {zeilen.length === 1 ? 'Ein Vorschlag' : `${zeilen.length} Vorschläge`}
        {erledigt > 0 && ` · ${erledigt} erledigt`}
      </div>
      <ul className="space-y-1">
        {zeilen.map((z) => (
          <li key={z.id} className="flex items-start gap-2 text-xs">
            <input
              type="checkbox"
              className="mt-0.5"
              disabled={z.status !== 'OPEN' || !beschreibbar}
              checked={z.status === 'ACCEPTED' || haken.has(z.id)}
              onChange={(e) =>
                setHaken((h) => {
                  const n = new Set(h)
                  if (e.target.checked) n.add(z.id)
                  else n.delete(z.id)
                  return n
                })
              }
            />
            <span className={z.status === 'REJECTED' || z.status === 'DROPPED' ? 'text-muted-foreground line-through' : ''}>
              {z.position && <span className="text-muted-foreground">{z.position.label} · </span>}
              {titelVon(z)}
              {z.status === 'ACCEPTED' && <span className="ml-1 text-emerald-700 dark:text-emerald-400">übernommen</span>}
            </span>
          </li>
        ))}
      </ul>
      {offen.length > 0 && beschreibbar && (
        <div className="mt-2 flex gap-2">
          <button type="button" className="rounded-md bg-primary px-2.5 py-1 text-xs text-primary-foreground disabled:opacity-50" disabled={laeuft || haken.size === 0} onClick={() => void tu(() => kiUebernehmen(kalkulationId, runId, [...haken], []))}>
            Übernehmen ({haken.size})
          </button>
          <button type="button" className="rounded-md border border-border px-2.5 py-1 text-xs disabled:opacity-50" disabled={laeuft} onClick={() => void tu(() => kiVerwerfen(kalkulationId, runId, offen.map((z) => z.id)))}>
            Alle verwerfen
          </button>
        </div>
      )}
      {fehler && <p className="mt-1 text-xs text-destructive">{fehler}</p>}
    </div>
  )
}
